// src/services/events/eventsService.js
import { ref, push, set, get, remove } from "firebase/database";
import { rtdb } from "../firebase/rtdb";

const DEFAULT_BANNER =
    "https://images.unsplash.com/photo-1492684223066-81342ee5ff30";

function toMs(dateStr, timeStr) {
    if (!dateStr) return null;
    const iso = timeStr ? `${dateStr}T${timeStr}:00` : `${dateStr}T00:00:00`;
    const d = new Date(iso);
    const ms = d.getTime();
    return Number.isNaN(ms) ? null : ms;
}

function slugify(str = "") {
    return String(str)
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9\s-]/g, "")
        .trim()
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-");
}

function isBlobUrl(url = "") {
    return typeof url === "string" && url.startsWith("blob:");
}

function makeId(prefix = "id") {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
        return `${prefix}_${crypto.randomUUID()}`;
    }
    return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function cleanString(value) {
    return String(value || "").trim();
}

function normalizeBanner(state) {
    const bannerUrlCandidate =
        (state.identidade?.banner?.mode === "url" &&
            cleanString(state.identidade?.banner?.url)) ||
        state.identidade?.banner?.previewUrl ||
        "";

    return bannerUrlCandidate && !isBlobUrl(bannerUrlCandidate)
        ? bannerUrlCandidate
        : DEFAULT_BANNER;
}

function buildTicketStructures(ingressos = []) {
    const ticketTypes = {};
    const ticketLots = {};

    let minPrice = null;
    let maxPrice = null;

    for (const ingresso of ingressos) {
        const ticketTypeId = ingresso.id || makeId("ticketType");
        const lotId = `${ticketTypeId}_lot_1`;
        const price = Number(ingresso.preco || 0);

        if (price > 0) {
            minPrice = minPrice == null ? price : Math.min(minPrice, price);
            maxPrice = maxPrice == null ? price : Math.max(maxPrice, price);
        }

        ticketTypes[ticketTypeId] = {
            ticketTypeId,
            name: cleanString(ingresso.nome),
            visibility: ingresso.visibilidade || "publico",
            feeAbsorbedByOrganizer: !!ingresso.taxaAbsorvidaOrganizador,
            status: "active",
            sortOrder: 0,
            createdAt: Date.now(),
            updatedAt: Date.now(),
        };

        ticketLots[lotId] = {
            lotId,
            ticketTypeId,
            name: cleanString(ingresso.lote || "Lote 1"),
            price,
            quantity: Number(ingresso.quantidade || 0),
            soldCount: 0,
            salesStartMs: ingresso.vendas?.inicio
                ? toMs(ingresso.vendas.inicio, "00:00")
                : null,
            salesEndMs: ingresso.vendas?.fim
                ? toMs(ingresso.vendas.fim, "23:59")
                : null,
            status: "active",
            createdAt: Date.now(),
            updatedAt: Date.now(),
        };
    }

    return {
        ticketTypes,
        ticketLots,
        minTicketPrice: minPrice ?? 0,
        maxTicketPrice: maxPrice ?? 0,
    };
}

function buildKeyedList(values = [], prefix = "item", mapper = (v) => v) {
    const out = {};
    values.forEach((value, index) => {
        const mapped = mapper(value, index);
        if (!mapped) return;
        const id = mapped.id || `${prefix}_${index + 1}`;
        out[id] = { ...mapped };
    });
    return out;
}

function normalizeEventBundle(state, options = {}) {
    const now = Date.now();
    const status = options.status || "draft";
    const organizerId =
        options.organizerId || options.ownerUid || options.createdByUid || "";
    const createdByUid = options.createdByUid || organizerId || "";

    const title = cleanString(state.identidade?.titulo);
    const slug = slugify(title);
    const bannerUrl = normalizeBanner(state);

    const startAtMs = toMs(
        state.dataLocal?.dataInicio,
        state.dataLocal?.horaInicio,
    );
    const endAtMs = state.dataLocal?.dataFim
        ? toMs(state.dataLocal?.dataFim, state.dataLocal?.horaFim)
        : null;

    const { ticketTypes, ticketLots, minTicketPrice, maxTicketPrice } =
        buildTicketStructures(state.ingressos || []);

    const tags = buildKeyedList(
        state.identidade?.tags || [],
        "tag",
        (label, index) => ({
            id: `tag_${index + 1}`,
            label: cleanString(label),
            position: index,
        }),
    );

    const lineup = buildKeyedList(
        state.descricao?.lineup || [],
        "lineup",
        (label, index) => ({
            id: `lineup_${index + 1}`,
            label: cleanString(label),
            position: index,
        }),
    );

    const physicalPoints = buildKeyedList(
        state.vendas?.pontosFisicos || [],
        "point",
        (label, index) => ({
            id: `point_${index + 1}`,
            name: cleanString(label),
            active: true,
            position: index,
        }),
    );

    const commissaries = buildKeyedList(
        state.vendas?.comissarios || [],
        "commissary",
        (c, index) => ({
            id: c?.id || `commissary_${index + 1}`,
            name: cleanString(c?.nome),
            phone: cleanString(c?.phone),
            active: true,
            createdAt: now,
        }),
    );

    const eventCore = {
        organizerId,
        createdByUid,
        status,
        title,
        category: cleanString(state.identidade?.categoria),
        classification: cleanString(state.identidade?.classificacao || "18"),
        description: cleanString(state.descricao?.texto),
        notes: cleanString(state.descricao?.observacoes),
        bannerUrl,
        gatesOpenText: state.dataLocal?.aberturaPortoes
            ? `Abertura dos portões às ${state.dataLocal.aberturaPortoes}`
            : "",
        timing: {
            startAtMs,
            endAtMs,
        },
        location: {
            name: cleanString(state.dataLocal?.local?.nome),
            address: cleanString(state.dataLocal?.local?.endereco),
            reference: cleanString(state.dataLocal?.local?.referencia),
            city: cleanString(state.dataLocal?.local?.cidade),
            state: cleanString(state.dataLocal?.local?.estado),
            mapsUrl: cleanString(state.dataLocal?.local?.mapsUrl),
        },
        rules: {
            allowMinors: !!state.regras?.permiteMenores,
            importantNotice: cleanString(state.regras?.avisoImportante),
            minorsDetails: cleanString(state.regras?.detalhesMenores),
            refundPolicy: cleanString(state.regras?.politicaReembolso || "padrao"),
            refundCustomText: cleanString(state.regras?.reembolsoPersonalizado),
        },
        salesSettings: {
            limitPerUser: Number(state.vendas?.limitePorUsuario || 2),
            onurbFeePercent: Number(state.vendas?.taxaOnurbPercent || 3),
        },
        seo: {
            slug,
        },
        stats: {
            ticketTypesCount: Object.keys(ticketTypes).length,
            lotsCount: Object.keys(ticketLots).length,
            soldCount: 0,
            grossRevenue: 0,
            minTicketPrice,
            maxTicketPrice,
        },
        updatedAtMs: now,
    };

    return {
        eventCore,
        tags,
        lineup,
        physicalPoints,
        commissaries,
        ticketTypes,
        ticketLots,
        publicIndex:
            status === "online"
                ? {
                    title,
                    slug,
                    bannerUrl,
                    city: eventCore.location.city,
                    state: eventCore.location.state,
                    startAtMs,
                    status,
                    classification: eventCore.classification,
                    category: eventCore.category,
                    minTicketPrice,
                    maxTicketPrice,
                }
                : null,
        createdAtMs: now,
        publishedAtMs: status === "online" ? now : null,
    };
}

async function writeEventBundle(eventId, bundle, { previousOrganizerId = null, isCreate = false } = {}) {
    const eventRef = ref(rtdb, `events/${eventId}`);
    const prevSnap = isCreate ? null : await get(eventRef);
    const prev = prevSnap?.exists() ? prevSnap.val() : null;

    const createdAtMs = prev?.createdAtMs || bundle.createdAtMs || Date.now();
    const publishedAtMs =
        prev?.publishedAtMs ||
        (bundle.publicIndex ? bundle.publishedAtMs || Date.now() : null);

    await set(eventRef, {
        eventId,
        ...bundle.eventCore,
        createdAtMs,
        publishedAtMs,
    });

    await Promise.all([
        set(ref(rtdb, `eventTags/${eventId}`), Object.keys(bundle.tags).length ? bundle.tags : null),
        set(ref(rtdb, `eventLineup/${eventId}`), Object.keys(bundle.lineup).length ? bundle.lineup : null),
        set(
            ref(rtdb, `eventPhysicalPoints/${eventId}`),
            Object.keys(bundle.physicalPoints).length ? bundle.physicalPoints : null,
        ),
        set(
            ref(rtdb, `eventCommissaries/${eventId}`),
            Object.keys(bundle.commissaries).length ? bundle.commissaries : null,
        ),
        set(
            ref(rtdb, `ticketTypes/${eventId}`),
            Object.keys(bundle.ticketTypes).length ? bundle.ticketTypes : null,
        ),
        set(
            ref(rtdb, `ticketLots/${eventId}`),
            Object.keys(bundle.ticketLots).length ? bundle.ticketLots : null,
        ),
        set(ref(rtdb, `eventsByOrganizer/${bundle.eventCore.organizerId}/${eventId}`), true),
        set(
            ref(rtdb, `publicEventIndex/${eventId}`),
            bundle.publicIndex ? bundle.publicIndex : null,
        ),
    ]);

    if (
        previousOrganizerId &&
        bundle.eventCore.organizerId &&
        previousOrganizerId !== bundle.eventCore.organizerId
    ) {
        await remove(ref(rtdb, `eventsByOrganizer/${previousOrganizerId}/${eventId}`));
    }

    return eventId;
}

export async function createEventRtdb(state, options = {}) {
    const bundle = normalizeEventBundle(state, options);
    const newRef = push(ref(rtdb, "events"));
    const eventId = newRef.key;

    if (!eventId) {
        throw new Error("Não foi possível gerar o eventId.");
    }

    return writeEventBundle(eventId, bundle, { isCreate: true });
}

export async function updateEventRtdb(eventId, state, options = {}) {
    if (!eventId) {
        throw new Error("updateEventRtdb: eventId é obrigatório");
    }

    const prevSnap = await get(ref(rtdb, `events/${eventId}`));
    const previousOrganizerId = prevSnap.exists()
        ? prevSnap.val()?.organizerId || null
        : null;

    const bundle = normalizeEventBundle(state, options);
    return writeEventBundle(eventId, bundle, { previousOrganizerId });
}